import { useState, useEffect, useMemo } from "react";
import { startOfWeek, endOfWeek, formatISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { AttendanceHeader } from "@/components/attendance/AttendanceHeader";
import { AttendanceSearchAndFilter } from "@/components/attendance/AttendanceSearchAndFilter";
import { AttendanceTable } from "@/components/attendance/AttendanceTable";
import { AttendancePagination } from "@/components/attendance/AttendancePagination";
import { toast } from "sonner";
import { useUserStore } from '@/store/userStore';
import { useClientsSupabaseStore } from '@/store/api/clientsSupabaseStore';
import { getAttendanceByBookings, createAttendance, updateAttendance, deleteAttendance } from '@/services/edgeFunctionWrappers/attendanceManagementWrapper';
import { getBookingsWithFilters, getBookingByClassAndUser, deleteBooking } from '@/services/edgeFunctionWrappers/bookingsManagementWrapper';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AttendeeData, BookingData } from "@/types/types";
import { DateRange } from "react-day-picker";

const getDefaultWeekRange = (): DateRange => {
  const today = new Date();
  return {
    from: startOfWeek(today, { weekStartsOn: 1 }),
    to: endOfWeek(today, { weekStartsOn: 1 }),
  };
};

// Plafond de la requête « journées complètes » utilisée pour compter les
// participants d'un cours. La pagination porte sur les réservations, pas sur
// les cours : sans cette seconde lecture, un cours à cheval sur deux pages
// n'affichait que la fraction visible (« 6/20 » pour 18 inscrits).
const FULL_RANGE_FETCH_LIMIT = 1000;

const mapBookingRow = (b: any): BookingData => ({
  id: b.id,
  userId: b.user_id,
  className: b.scheduled_classes?.name ?? "",
  coach: b.scheduled_classes?.instructor ?? "",
  date: b.class_date,
  time: b.time ?? "",
  duration: b.scheduled_classes?.duration ? String(b.scheduled_classes.duration) : "",
  status: b.status,
  classId: b.class_id,
  capacity: b.scheduled_classes?.capacity ?? 0,
});

const Attendance = () => {
  const { t } = useTranslation(["common", "bookingui", "adminui3"]);
  const gymId = useUserStore((state) => state.gymId);
  const clientsStore = useClientsSupabaseStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all"|"upcoming"|"completed"|"cancelled"|string>("upcoming");
  const [attendeesData, setAttendeesData] = useState<{[key: string]: AttendeeData[]}>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletionData, setDeletionData] = useState<{classId: string, userId: string, classDate: string, userName: string} | null>(null);
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultWeekRange);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Optimized Map cache for O(1) client lookups
  const clientsMap = useMemo(() => {
    return new Map(clientsStore.clients.map(c => [c.id, c]));
  }, [clientsStore.clients]);

  // FETCH BOOKINGS
  const fetchBookings = async () => {
    if (!gymId) {
      setBookings([]);
      setTotalCount(0);
      setAttendeesData({});
      return;
    }
    setIsLoading(true);
    try {
      const { data, error, totalCount: count } = await getBookingsWithFilters(gymId, {
        dateFrom: dateRange.from ? formatISO(dateRange.from, { representation: "date" }) : undefined,
        dateTo: dateRange.to ? formatISO(dateRange.to, { representation: "date" }) : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });

      if (error) throw error;

      const mapped = (data ?? []).map(mapBookingRow);
      setBookings(mapped);
      setTotalCount(count);
      await fetchAttendeesData(mapped);
    } catch (err) {
      toast.error(t("attendance.loadBookingsError", { ns: "bookingui" }));
      setBookings([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // FETCH ATTENDEES with optimized client cache
  const fetchAttendeesData = async (source: BookingData[] = bookings) => {
    try {
      if (!source.length || !gymId) {
        setAttendeesData({});
        return;
      }

      // La page ne contient qu'une tranche de réservations : on relit toutes
      // celles des journées affichées pour que le compteur « inscrits/capacité »
      // et la liste dépliée portent sur le cours entier, pas sur la page.
      const pageDates = source.map(b => b.date).filter(Boolean).sort();
      const { data: fullRangeRows, error: fullRangeError, totalCount: fullRangeCount } =
        await getBookingsWithFilters(gymId, {
          dateFrom: pageDates[0],
          dateTo: pageDates[pageDates.length - 1],
          status: statusFilter !== "all" ? statusFilter : undefined,
          limit: FULL_RANGE_FETCH_LIMIT,
          offset: 0,
        });
      if (fullRangeError) throw fullRangeError;

      if (fullRangeCount > FULL_RANGE_FETCH_LIMIT) {
        console.warn(
          `[Attendance] ${fullRangeCount} réservations sur les journées affichées, ` +
          `seules les ${FULL_RANGE_FETCH_LIMIT} premières sont comptées.`
        );
      }

      // Repli sur la page si la relecture ne renvoie rien d'exploitable.
      const allRows: BookingData[] = fullRangeRows?.length
        ? fullRangeRows.map(mapBookingRow)
        : source;

      const userIds = [...new Set(allRows.map(b => b.userId))];

      // Check which clients are missing from cache
      const missingUserIds = userIds.filter(id => !clientsMap.has(id));
      if (missingUserIds.length > 0) {
        await clientsStore.fetchClientsByIds(missingUserIds);
      }

      const bookingIds = allRows.map(b => b.id);
      const { data: attendanceData, error: attendanceError } = await getAttendanceByBookings(gymId, bookingIds);
      if (attendanceError) throw attendanceError;

      const attendanceMap = new Map();
      (attendanceData ?? []).forEach(item => attendanceMap.set(item.booking_id, item));

      const attendeesMap: {[key: string]: AttendeeData[]} = {};
      allRows.forEach(booking => {
        const client = clientsMap.get(booking.userId);
        const attendance = attendanceMap.get(booking.id);
        const key = `${booking.className}_${booking.date}_${booking.time || "00:00"}`;
        if (!attendeesMap[key]) attendeesMap[key] = [];
        attendeesMap[key].push({
          id: booking.id,
          userId: booking.userId,
          userFirstName: client?.firstName || "Unknown",
          userLastName: client?.lastName || "User",
          phone: client?.phone || null,
          profilePhoto: client?.profilePhotoUrl || null,
          attended: attendance?.attended || false,
          notes: attendance?.notes || null,
          status: booking.status || null,
          clothingSize: client?.clothingSize || null,
        });
      });
      setAttendeesData(attendeesMap);
    } catch (err) {
      toast.error(t("attendance.loadAttendeesError", { ns: "bookingui" }));
      setAttendeesData({});
    }
  };

  // MARQUER PRÉSENCE
  const markAttendance = async (bookingId: string, attended: boolean, notes?: string): Promise<boolean> => {
    if (!gymId) {
      toast.error(t("common.noGymSelected", { ns: "bookingui" }));
      return false;
    }
    setIsMarkingAttendance(true);
    try {
      const { data: existing, error: lookupError } = await getAttendanceByBookings(gymId, [bookingId]);
      if (lookupError) throw lookupError;

      // Les wrappers renvoient { data, error } sans jamais lever, et rendent
      // même { data: null, error: null } sur session expirée. Sans ces
      // contrôles, l'écran annonçait « Présence marquée » sans rien écrire.
      if (existing && existing.length > 0) {
        const { data, error } = await updateAttendance(existing[0].id, gymId, { attended, notes: notes || undefined });
        if (error) throw error;
        if (!data) throw new Error(t("attendancePage.markError", { ns: "adminui3" }));
      } else {
        const { data, error } = await createAttendance({ booking_id: bookingId, attended, notes, gym_id: gymId });
        if (error) throw error;
        if (!data) throw new Error(t("attendancePage.markError", { ns: "adminui3" }));
      }
      await fetchAttendeesData();
      return true;
    } catch (err) {
      console.error("[Attendance] markAttendance:", err);
      toast.error(t("attendancePage.updateError", { ns: "adminui3" }));
      return false;
    } finally {
      setIsMarkingAttendance(false);
    }
  };

  // SUPPRIMER CLIENT
  const confirmRemoveClientFromClass = (classId: string, userId: string, classDate: string, userName: string) => {
    setDeletionData({ classId, userId, classDate, userName });
    setIsDeleteDialogOpen(true);
    return Promise.resolve(false);
  };

  const handleRemoveClientFromClass = async (): Promise<boolean> => {
    if (!deletionData || !gymId) return false;
    try {
      const { classId, userId, classDate, userName } = deletionData;
      setIsDeleteDialogOpen(false);

      // Find the booking via edge function
      const { data: bookingData, error: findError } = await getBookingByClassAndUser(
        gymId,
        classId,
        userId,
        classDate
      );

      if (findError || !bookingData) {
        toast.error("No booking record found");
        return false;
      }

      // Delete attendance record if it exists
      const { data: attendanceRecords } = await getAttendanceByBookings(gymId, [bookingData.id]);
      if (attendanceRecords && attendanceRecords.length > 0) {
        await deleteAttendance(attendanceRecords[0].id, gymId);
      }

      // Delete the booking via edge function
      const { error: deleteError } = await deleteBooking(gymId, bookingData.id);
      if (deleteError) throw deleteError;

      toast.success(t("attendance.removeClientSuccess", { ns: "bookingui", userName }));
      await fetchBookings();
      await fetchAttendeesData();
      return true;
    } catch (err) {
      toast.error(t("attendance.removeClientError", { ns: "bookingui" }));
      return false;
    } finally {
      setDeletionData(null);
    }
  };

  const getClassCapacity = (booking: BookingData) => {
    const scheduledClass = bookings.find(sc => sc.id === booking.id);
    return scheduledClass?.capacity || 0;
  };

  // Bookings actuels pour un cours
  const getCurrentBookings = (className: string, date: string, time?: string) => {
    const key = `${className}_${date}_${time || "00:00"}`;
    return attendeesData[key]?.length || 0;
  };

  // Preload clients if cache is empty
  // gymId en dépendance : au changement de salle le cache client est vidé et
  // il faut le recharger, sinon la page reste sur les adhérents de l'ancienne.
  useEffect(() => {
    if (gymId && clientsStore.clients.length === 0) {
      clientsStore.fetchClients();
    }
  }, [gymId]);

  // Le changement de salle doit ramener la pagination au début : la page 4 de
  // la salle A n'a aucun sens dans la salle B.
  useEffect(() => { setCurrentPage(1); }, [gymId]);

  useEffect(() => { fetchBookings(); }, [gymId, dateRange, statusFilter, currentPage]);

  const filteredBookings = useMemo(() => {
    let result = bookings;
    if (searchTerm.trim()) {
      result = result.filter(booking => {
        const matchesClassName = booking.className.toLowerCase().includes(searchTerm.toLowerCase());
        const bookingKey = `${booking.className}_${booking.date}_${booking.time || "00:00"}`;
        const attendees = attendeesData[bookingKey] || [];
        const matchesAttendee = attendees.some(attendee =>
          `${attendee.userFirstName} ${attendee.userLastName}`.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return matchesClassName || matchesAttendee;
      });
    }
    return result;
  }, [bookings, searchTerm, attendeesData]);

  return (
    <div className="min-h-screen flex flex-col w-full">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
        <AttendanceHeader bookings={bookings} isLoading={isLoading} />
        <Card className="animate-slide-in">
          <CardContent className="pt-6">
            <AttendanceSearchAndFilter
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            <AttendanceTable
              bookings={filteredBookings}
              isLoading={isLoading}
              getClassCapacity={getClassCapacity}
              getCurrentBookings={getCurrentBookings}
              attendeesData={attendeesData}
              onRemoveClient={confirmRemoveClientFromClass}
              onMarkAttendance={markAttendance}
              onRefreshData={fetchBookings}
            />
            <AttendancePagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          </CardContent>
        </Card>
      </main>
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("attendance.confirmRemoveTitle", { ns: "bookingui" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("attendance.confirmRemoveDescription", { ns: "bookingui" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveClientFromClass} 
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Attendance;
